import { Boundaries } from "@planzone/core";
import { Identifiable, Resource } from "@planzone/dto";
import { Event } from "@planzone/events";
import { reduceResource } from "../../common/resource";
import { State } from "./types";

// Keeps the resources embedded in the tree in sync with edits made anywhere (OBS page, members page,
// resource sheet), the way retrievePlanzoneResources does for the members list.

function mapResources(
  state: State,
  map: (resource: Identifiable<Resource>) => Identifiable<Resource>,
): State {
  let hasChanged = false;
  const organizationalUnits = state.organizationalUnits.map((unit) => {
    if (!unit.resources) return unit;

    const resources = unit.resources.map(map);

    if (resources.every((resource, i) => resource === unit.resources?.[i])) {
      return unit;
    }

    hasChanged = true;
    return { ...unit, resources };
  });

  return hasChanged ? { ...state, organizationalUnits } : state;
}

// A resource is listed under the unit it belongs to: it stays in place while its unit is unchanged,
// joins the end of its new unit's resources, or leaves the tree when it has no unit anymore (or one
// not loaded yet, which will embed it once fetched).
function placeResource(state: State, resource: Identifiable<Resource>): State {
  const unitUuid = resource.organizationalUnit?.uuid;

  return {
    ...state,
    organizationalUnits: state.organizationalUnits.map((unit) => {
      const resources = unit.resources ?? [];
      const isListed = resources.some(({ uuid }) => uuid === resource.uuid);

      return unit.uuid === unitUuid
        ? {
            ...unit,
            resources: isListed
              ? resources.map((r) => (r.uuid === resource.uuid ? resource : r))
              : [...resources, resource],
          }
        : isListed
          ? {
              ...unit,
              resources: resources.filter(({ uuid }) => uuid !== resource.uuid),
            }
          : unit;
    }),
  };
}

function findResource(
  state: State,
  uuid: string,
): Identifiable<Resource> | undefined {
  return state.organizationalUnits
    .flatMap((unit) => unit.resources ?? [])
    .find((resource) => resource.uuid === uuid);
}

function handleEditResourceRequest(
  state: State,
  { resource, nextResource }: Boundaries["editResource"]["request"],
): State {
  return placeResource(state, {
    ...(findResource(state, resource.uuid) ?? resource),
    ...nextResource,
    uuid: resource.uuid,
  });
}

// The PATCH response carries no subordinates, so the ones already listed are kept.
function handleEditResourceSuccess(
  state: State,
  resource: Boundaries["editResource"]["success"],
): State {
  const current = findResource(state, resource.uuid);

  return placeResource(state, {
    ...current,
    ...resource,
    subordinates: current?.subordinates ?? resource.subordinates,
  });
}

function handleEditResourceFailure(
  state: State,
  { resource }: Boundaries["editResource"]["failure"],
): State {
  return placeResource(state, resource);
}

function placeResources(
  state: State,
  resources: Identifiable<Resource>[],
): State {
  return resources.reduce(
    (nextState, resource) =>
      placeResource(nextState, {
        ...(findResource(nextState, resource.uuid) ?? resource),
        organizationalUnit: resource.organizationalUnit,
      }),
    state,
  );
}

function handleAssignManagerToResourceSuccess(
  state: State,
  { resource, manager }: Boundaries["assignManagerToResource"]["success"],
): State {
  return mapResources(state, (r) =>
    r.uuid === manager.uuid
      ? { ...r, subordinates: [...(r.subordinates ?? []), resource] }
      : r,
  );
}

function handleUnAssignManagerFromResourceSuccess(
  state: State,
  { resource, manager }: Boundaries["unAssignManagerFromResource"]["success"],
): State {
  return mapResources(state, (r) =>
    r.uuid === manager.uuid
      ? {
          ...r,
          subordinates: (r.subordinates ?? []).filter(
            ({ uuid }) => uuid !== resource.uuid,
          ),
        }
      : r,
  );
}

function handleAssignEveryoneToManagerSuccess(
  state: State,
  { manager, subordinates }: Boundaries["assignEveryoneToManager"]["success"],
): State {
  return mapResources(state, (r) =>
    r.uuid === manager.uuid
      ? { ...r, subordinates: [...(r.subordinates ?? []), ...subordinates] }
      : r,
  );
}

function handleSubordinatesRemoval(
  state: State,
  { manager, subordinateUuids }: Boundaries["removeSubordinates"]["success"],
): State {
  return mapResources(state, (r) =>
    r.uuid === manager.uuid
      ? {
          ...r,
          subordinates: (r.subordinates ?? []).filter(
            ({ uuid }) => !subordinateUuids.includes(uuid),
          ),
        }
      : r,
  );
}

// The unit column reads its label from the resource, so a renamed unit is renamed on its resources.
function renameResourcesUnit(state: State, uuid: string, name: string): State {
  return mapResources(state, (resource) =>
    resource.organizationalUnit?.uuid === uuid &&
    resource.organizationalUnit.name !== name
      ? {
          ...resource,
          organizationalUnit: { ...resource.organizationalUnit, name },
        }
      : resource,
  );
}

// Tags, cost profile, avatar…: the shared per-resource reducer, applied to every embedded resource.
function propagateResourceEvent(state: State, event: Event): State {
  return mapResources(state, (resource) => reduceResource(resource, event));
}

export {
  handleEditResourceRequest,
  handleEditResourceSuccess,
  handleEditResourceFailure,
  placeResources,
  handleAssignManagerToResourceSuccess,
  handleUnAssignManagerFromResourceSuccess,
  handleAssignEveryoneToManagerSuccess,
  handleSubordinatesRemoval,
  renameResourcesUnit,
  propagateResourceEvent,
};
